/**
 * Link Line - Konva.js Link Rendering
 *
 * FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/LinkLine.js
 *
 * Renders links (connections) between SAX component slots as bezier curves with arrows
 */

import Konva from 'konva';
import { calculateOrthogonalRoute, calculateRoundedCorners } from './orthogonalRouter.js';
import { getCrossingsForLink, shouldShowBridge, calculateBridgeArc } from './crossingDetector.js';

export class LinkLine {
  constructor(link, componentNodes, saxStore = null, config = {}) {
    this.link = link;
    this.componentNodes = componentNodes;
    this.saxStore = saxStore;

    // Determine wire color based on data type
    const wireColor = this.getWireColor();

    this.config = {
      strokeWidth: 2,
      stroke: wireColor,
      selectedStroke: '#EF4444',
      hitStrokeWidth: 10,
      existingWirePaths: [], // Wire paths to avoid when routing
      ...config,
    };

    this.group = null;
    this.line = null;
    this.arrow = null;
    this.bridges = []; // Bridge arc shapes for crossings

    this.buildLine();
  }

  /**
   * Get wire color based on slot data type
   */
  getWireColor() {
    // Get the output slot (from_slot) to determine the data type
    const fromNode = this.componentNodes[this.link.from_component];
    if (!fromNode) {return '#94A3B8';} // Default gray

    const slotInfo = fromNode.getSlotInfo(this.link.from_slot);
    if (!slotInfo || !slotInfo.slot) {return '#94A3B8';}

    const slotType = slotInfo.slot.type;
    const typeColors = {
      'float': '#4CAF50',   // Green for float (analog values)
      'int': '#2196F3',     // Blue for int (digital values)
      'bool': '#FF9800',    // Orange for bool (binary states)
    };

    return typeColors[slotType] || '#9E9E9E'; // Gray for 'other' or undefined
  }

  /**
   * Build the Konva group with line and arrow
   */
  buildLine() {
    // Create main group
    this.group = new Konva.Group({
      id: this.link.id,
    });

    // Get positions of from/to slots
    const fromNode = this.componentNodes[this.link.from_component];
    const toNode = this.componentNodes[this.link.to_component];

    // Get slot positions, handling virtual slots on folders
    const fromPos = this.getSlotPositionWithVirtualSupport(
      fromNode,
      this.link.from_component,
      this.link.from_slot,
      'output',
    );
    const toPos = this.getSlotPositionWithVirtualSupport(
      toNode,
      this.link.to_component,
      this.link.to_slot,
      'input',
    );

    if (!fromPos || !toPos) {
      console.warn('[LinkLine] Link references missing slot:', this.link, {
        fromPos,
        toPos,
      });
      return;
    }

    // Calculate orthogonal route with obstacle avoidance
    this.waypoints = calculateOrthogonalRoute(
      fromPos,
      toPos,
      this.componentNodes,
      {
        exitOffset: 30,
        entryOffset: 30,
        gridSize: 10,
        canvasWidth: 4000,
        canvasHeight: 4000,
        offsetX: 2000,
        offsetY: 2000,
        existingWirePaths: this.config.existingWirePaths, // Prevent routing through existing wires
      },
    );

    // Convert waypoints to flat array with rounded corners
    const points = [];
    this.waypoints.forEach((wp) => {
      points.push(wp.x, wp.y);
    });

    // Create the line with sharp right-angle corners
    this.line = new Konva.Line({
      points: points,
      stroke: this.config.stroke,
      strokeWidth: this.config.strokeWidth,
      lineCap: 'round',
      lineJoin: 'miter',  // Sharp corners for orthogonal routing
      tension: 0,
    });

    // Add invisible wider line for easier clicking
    const hitLine = new Konva.Line({
      points: points,
      stroke: 'transparent',
      strokeWidth: this.config.hitStrokeWidth,
      lineCap: 'round',
      lineJoin: 'round',
      bezier: true,
      tension: 0,
    });

    this.group.add(hitLine);
    this.group.add(this.line);

    // Add arrow at the end
    this.arrow = this.createArrow(toPos);
    if (this.arrow) {
      this.group.add(this.arrow);
    }

    // Add event handlers
    this.addEventHandlers();

    return this.group;
  }

  /**
   * Create arrow shape at the end of the line
   */
  createArrow(toPos) {
    // Get the direction of the arrow (pointing into the slot)
    const arrowSize = 8;

    // Arrow pointing left (into input slot)
    const arrow = new Konva.Line({
      points: [
        toPos.x, toPos.y,
        toPos.x + arrowSize, toPos.y - arrowSize / 2,
        toPos.x + arrowSize, toPos.y + arrowSize / 2,
      ],
      fill: this.config.stroke,
      stroke: this.config.stroke,
      strokeWidth: 1,
      closed: true,
    });

    return arrow;
  }

  /**
   * Add event handlers
   */
  addEventHandlers() {
    // Hover effects - make line thicker and slightly brighter
    this.group.on('mouseenter', () => {
      document.body.style.cursor = 'pointer';
      this.line.strokeWidth(this.config.strokeWidth + 2);
      // Add subtle glow effect
      this.line.shadowColor(this.config.stroke);
      this.line.shadowBlur(8);
      this.line.shadowOpacity(0.6);
      if (this.arrow) {
        this.arrow.shadowColor(this.config.stroke);
        this.arrow.shadowBlur(8);
        this.arrow.shadowOpacity(0.6);
      }
      this.group.getLayer()?.batchDraw();
    });

    this.group.on('mouseleave', () => {
      document.body.style.cursor = 'default';
      if (!this.isSelected()) {
        this.line.strokeWidth(this.config.strokeWidth);
        this.line.shadowColor('transparent');
        this.line.shadowBlur(0);
        if (this.arrow) {
          this.arrow.shadowColor('transparent');
          this.arrow.shadowBlur(0);
        }
      }
      this.group.getLayer()?.batchDraw();
    });
  }

  /**
   * Get slot position with support for virtual slots on folders
   * This handles the case where a link endpoint is a child component inside a folder,
   * but we need to show the link terminating at the folder's virtual slot instead.
   */
  getSlotPositionWithVirtualSupport(node, componentId, slotName, slotType) {
    // If node exists, try to get the position directly
    if (node) {
      const directPos = node.getSlotPosition(slotName);
      if (directPos) {
        return directPos;
      }
    }

    // Node doesn't exist or slot not found - component might be inside a folder
    // Find the folder that contains this component
    if (this.saxStore) {
      const component = this.saxStore.getComponent(componentId);

      if (!component) {return null;}

      const parentId = component.parent_id;
      if (parentId) {
        // Component is inside a folder - get the folder node
        const folderNode = this.componentNodes[parentId];

        if (folderNode && folderNode.getComponent().type === 'sys::Folder') {
          // Compute virtual slots for this folder
          const virtualSlots = this.saxStore.computeFolderVirtualSlots(parentId);

          // Find the virtual slot that corresponds to this link endpoint
          const virtualSlotArray = slotType === 'input' ? virtualSlots.inputs : virtualSlots.outputs;
          const virtualSlot = virtualSlotArray.find((vs) => {
            // Check if this virtual slot represents the target component and slot
            if (slotType === 'input') {
              return vs.target_component === componentId && vs.target_slot === slotName;
            } else {
              return vs.source_component === componentId && vs.source_slot === slotName;
            }
          });

          if (virtualSlot) {
            // Found the virtual slot - get its position using the virtual slot's name
            const pos = folderNode.getSlotPosition(virtualSlot.name);
            return pos;
          }
        }
      }
    }

    return null;
  }

  /**
   * Update link positions when components move
   */
  updatePositions() {
    const fromNode = this.componentNodes[this.link.from_component];
    const toNode = this.componentNodes[this.link.to_component];

    if (!fromNode || !toNode) {return;}

    // Get slot positions, handling virtual slots on folders
    const fromPos = this.getSlotPositionWithVirtualSupport(
      fromNode,
      this.link.from_component,
      this.link.from_slot,
      'output',
    );
    const toPos = this.getSlotPositionWithVirtualSupport(
      toNode,
      this.link.to_component,
      this.link.to_slot,
      'input',
    );

    if (!fromPos || !toPos) {return;}

    // Recalculate orthogonal route
    this.waypoints = calculateOrthogonalRoute(
      fromPos,
      toPos,
      this.componentNodes,
      {
        exitOffset: 30,
        entryOffset: 30,
        gridSize: 10,
        canvasWidth: 4000,
        canvasHeight: 4000,
        offsetX: 2000,
        offsetY: 2000,
      },
    );

    // Convert to rounded corner points
    const points = calculateRoundedCorners(this.waypoints, 10);

    // Update line points
    this.line.points(points);

    // Update hit area
    const hitLine = this.group.children[0];
    if (hitLine) {
      hitLine.points(points);
    }

    // Update arrow position
    if (this.arrow) {
      this.arrow.destroy();
      this.arrow = this.createArrow(toPos);
      this.group.add(this.arrow);
    }

    // Note: batchDraw is called externally to batch multiple link updates
  }

  /**
   * Update bridge rendering based on crossings
   * @param {Array} crossings - All crossings detected in the canvas
   * @param {Map} zOrder - Z-order map for all links
   */
  updateBridges(crossings, zOrder) {
    // Remove existing bridges
    this.bridges.forEach((bridge) => bridge.destroy());
    this.bridges = [];

    if (!crossings || !zOrder) {return;}

    // Get crossings that involve this link
    const linkCrossings = getCrossingsForLink(this.link.id, crossings);

    // For each crossing, add a bridge if this link goes under
    linkCrossings.forEach((crossing) => {
      if (shouldShowBridge(this.link.id, crossing, zOrder)) {
        const bridgeArc = calculateBridgeArc(crossing, this.waypoints, 8);

        if (bridgeArc) {
          // Create a small arc that "jumps over" the crossing wire
          // We'll draw this as a quadratic curve
          const bridge = new Konva.Line({
            points: [
              bridgeArc.startPoint.x,
              bridgeArc.startPoint.y,
              bridgeArc.arcPoint.x,
              bridgeArc.arcPoint.y,
              bridgeArc.endPoint.x,
              bridgeArc.endPoint.y,
            ],
            stroke: this.config.stroke,
            strokeWidth: this.config.strokeWidth,
            lineCap: 'round',
            lineJoin: 'round',
            bezier: true,
            tension: 0,
          });

          this.bridges.push(bridge);
          this.group.add(bridge);

          // Also need to "cut out" the original line at the crossing
          // This is complex - for now we'll just draw the bridge on top
          // In a more sophisticated implementation, we'd split the line into segments
        }
      }
    });
  }

  /**
   * Set selected state
   */
  setSelected(selected) {
    if (selected) {
      this.line.stroke(this.config.selectedStroke);
      this.line.strokeWidth(this.config.strokeWidth + 1);
      if (this.arrow) {
        this.arrow.fill(this.config.selectedStroke);
        this.arrow.stroke(this.config.selectedStroke);
      }
      // Update bridge colors
      this.bridges.forEach((bridge) => {
        bridge.stroke(this.config.selectedStroke);
      });
    } else {
      this.line.stroke(this.config.stroke);
      this.line.strokeWidth(this.config.strokeWidth);
      if (this.arrow) {
        this.arrow.fill(this.config.stroke);
        this.arrow.stroke(this.config.stroke);
      }
      // Update bridge colors
      this.bridges.forEach((bridge) => {
        bridge.stroke(this.config.stroke);
      });
    }

    this.group.getLayer()?.batchDraw();
  }

  /**
   * Check if selected
   */
  isSelected() {
    return this.line.stroke() === this.config.selectedStroke;
  }

  /**
   * Show/hide link
   */
  setVisible(visible) {
    this.group.visible(visible);
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Destroy the link
   */
  destroy() {
    this.bridges.forEach((bridge) => bridge.destroy());
    this.bridges = [];
    this.group.destroy();
  }

  /**
   * Get the Konva group
   */
  getGroup() {
    return this.group;
  }

  /**
   * Get link ID
   */
  getId() {
    return this.link.id;
  }

  /**
   * Get link data
   */
  getLink() {
    return this.link;
  }

  /**
   * Bring to front
   */
  moveToTop() {
    this.group.moveToTop();
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Send to back
   */
  moveToBottom() {
    this.group.moveToBottom();
    this.group.getLayer()?.batchDraw();
  }

  /**
   * Animate link (pulse effect)
   */
  animate() {
    const anim = new Konva.Animation((frame) => {
      const scale = Math.sin((frame.time * 2 * Math.PI) / 1000) * 0.3 + 1;
      this.line.strokeWidth(this.config.strokeWidth * scale);
    }, this.group.getLayer());

    anim.start();

    // Stop after 2 seconds
    setTimeout(() => {
      anim.stop();
      this.line.strokeWidth(this.config.strokeWidth);
    }, 2000);
  }

  /**
   * Highlight link (for validation errors)
   */
  setError(hasError) {
    if (hasError) {
      this.line.stroke('#EF4444');
      this.line.dash([5, 5]);
      if (this.arrow) {
        this.arrow.fill('#EF4444');
        this.arrow.stroke('#EF4444');
      }
      // Update bridge colors
      this.bridges.forEach((bridge) => {
        bridge.stroke('#EF4444');
        bridge.dash([5, 5]);
      });
    } else {
      this.line.stroke(this.config.stroke);
      this.line.dash([]);
      if (this.arrow) {
        this.arrow.fill(this.config.stroke);
        this.arrow.stroke(this.config.stroke);
      }
      // Update bridge colors
      this.bridges.forEach((bridge) => {
        bridge.stroke(this.config.stroke);
        bridge.dash([]);
      });
    }

    this.group.getLayer()?.batchDraw();
  }

  /**
   * Get link midpoint (for labels or interaction points)
   */
  getMidpoint() {
    const points = this.line.points();
    const midIndex = Math.floor(points.length / 2);

    return {
      x: points[midIndex],
      y: points[midIndex + 1],
    };
  }

  /**
   * Check if link connects two components
   */
  connectsComponents(comp1Id, comp2Id) {
    return (
      (this.link.from_component === comp1Id && this.link.to_component === comp2Id) ||
      (this.link.from_component === comp2Id && this.link.to_component === comp1Id)
    );
  }

  /**
   * Check if link involves a component
   */
  involvesComponent(compId) {
    return this.link.from_component === compId || this.link.to_component === compId;
  }

  /**
   * Get connection info
   */
  getConnectionInfo() {
    return {
      from: {
        component: this.link.from_component,
        slot: this.link.from_slot,
      },
      to: {
        component: this.link.to_component,
        slot: this.link.to_slot,
      },
    };
  }

  /**
   * Get waypoints for this link (used for crossing detection)
   */
  getWaypoints() {
    return this.waypoints || [];
  }
}

export default LinkLine;
